/**
 * iter702 — Customer Display ad/promo rotator administration.
 *
 * Lets admins upload, caption, reorder, and toggle promo slides that
 * loop on the Customer Display window whenever the cart is empty. The
 * Customer Display itself is unauthenticated (it lives on a kiosk
 * monitor), so the actual promo *catalogue* is broadcast to it from
 * the authenticated Cashier POS via the existing BroadcastChannel.
 *
 * Drag-and-drop:
 *   • Drop image files onto the dotted upload zone — they're saved to
 *     `/api/uploads/promos/<file>` and inserted as new active slides.
 *   • Reorder slides by dragging them up/down; the order is persisted
 *     immediately so the screen reflects the new sequence on next open.
 *
 * Page is admin/manager-only (matches `_can_manage` in the backend).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  GripVertical,
  Image as ImageIcon,
  Plus,
  Trash2,
  Upload,
  Loader2,
  Eye,
  EyeOff,
  Save,
  Info,
  Monitor,
  Clock,
  Calendar,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import { resolveUploadUrl } from '@/lib/uploadUrl';
import { useBranchStore } from '@/stores/branchStore';

export default function CustomerDisplayPromosPage() {
  const { branches, fetchBranches } = useBranchStore();
  const [promos, setPromos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { fetchBranches?.(); }, [fetchBranches]);

  // ── Load ────────────────────────────────────────────────────────
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/customer-display/promos');
      setPromos(Array.isArray(data.items) ? data.items : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load promos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const activeCount = useMemo(() => promos.filter((p) => p.active).length, [promos]);

  // ── Upload (single or multi-file drop) ──────────────────────────
  const onPickFiles = () => fileInputRef.current?.click();

  const onFilesPicked = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const upRes = await api.post('/customer-display/promos/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        await api.post('/customer-display/promos', {
          image_url: upRes.data.image_url,
          caption: file.name.replace(/\.[^.]+$/, '').slice(0, 60),
          duration_seconds: 6,
          active: true,
        });
      }
      toast.success(`${files.length} slide${files.length === 1 ? '' : 's'} added`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDropFiles = (e) => {
    e.preventDefault();
    onFilesPicked(Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith('image/')));
  };

  // ── Per-row updates ─────────────────────────────────────────────
  const updateRow = (id, patch) => {
    setPromos((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch, _dirty: true } : p)));
  };

  const saveRow = async (promo) => {
    setSavingId(promo.id);
    try {
      const payload = {
        caption: promo.caption || null,
        subtitle: promo.subtitle || null,
        duration_seconds: Number(promo.duration_seconds) || 6,
        active: !!promo.active,
        branch_id: promo.branch_id || null,
        // iter705 — Schedule window. `|| null` so clearing the input
        // explicitly wipes the field server-side (backend whitelist
        // keeps the key on the PATCH update).
        start_at: promo.start_at || null,
        end_at: promo.end_at || null,
      };
      await api.patch(`/customer-display/promos/${promo.id}`, payload);
      toast.success('Slide saved');
      // Clear the dirty flag locally without a full reload.
      setPromos((prev) => prev.map((p) => (p.id === promo.id ? { ...p, _dirty: false } : p)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  const removeRow = async (promo) => {
    if (!window.confirm(`Delete slide "${promo.caption || promo.image_url}"?`)) return;
    try {
      await api.delete(`/customer-display/promos/${promo.id}`);
      toast.success('Slide deleted');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  // ── Drag-to-reorder ─────────────────────────────────────────────
  // Native HTML5 DnD — keeps the bundle slim and works on Safari/iPad.
  // We commit the new order to the backend on drop, then refresh state
  // from the reorder response so sort_order stays in lockstep.
  const onDragStart = (idx) => () => setDragIndex(idx);
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    if (idx !== dragOverIndex) setDragOverIndex(idx);
  };
  const onDragEnd = () => { setDragIndex(null); setDragOverIndex(null); };
  const onDrop = (idx) => async (e) => {
    e.preventDefault();
    if (dragIndex == null || dragIndex === idx) { onDragEnd(); return; }
    const next = [...promos];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(idx, 0, moved);
    setPromos(next);
    onDragEnd();
    try {
      await api.post('/customer-display/promos/reorder', { ids: next.map((p) => p.id) });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reorder failed');
      load();  // resync from server on failure.
    }
  };

  // ── Render ──────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6 space-y-5" data-testid="customer-display-promos-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Monitor size={22} className="text-primary" /> Customer Display · Promos
            </h1>
            <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
              Rotate up to a dozen full-screen images on the second-monitor display while the cashier waits for the next order.
              Drag to reorder; toggle a slide off to pause it without deleting.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500 hidden sm:inline">{activeCount} active / {promos.length} total</span>
            <Button variant="ghost" size="sm" onClick={() => setShowHelp(true)} data-testid="promos-help-btn">
              <Info size={14} className="mr-1" /> How does this look?
            </Button>
            <Button onClick={onPickFiles} disabled={uploading} className="bg-primary hover:bg-primary/90 text-white" data-testid="promos-add-btn">
              {uploading ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
              Add slide
            </Button>
          </div>
        </div>

        {/* Drop zone */}
        <Card>
          <CardContent className="p-0">
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDropFiles}
              className="border-2 border-dashed border-zinc-300 rounded-xl m-4 p-8 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
              onClick={onPickFiles}
              data-testid="promos-dropzone"
            >
              <Upload size={28} className="mx-auto mb-2 text-zinc-400" />
              <p className="text-sm font-medium text-zinc-700">Drop images here or click to browse</p>
              <p className="text-xs text-zinc-500 mt-1">JPG · PNG · WebP · GIF · max 5 MB each. Wide / 16:9 looks best.</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => { onFilesPicked(Array.from(e.target.files || [])); e.target.value = ''; }}
                data-testid="promos-file-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Slides list */}
        {loading ? (
          <div className="flex items-center justify-center py-12 text-zinc-500 text-sm">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading…
          </div>
        ) : promos.length === 0 ? (
          <Card>
            <CardContent className="p-10 text-center" data-testid="promos-empty-state">
              <ImageIcon size={32} className="mx-auto text-zinc-300 mb-3" />
              <p className="text-zinc-600 font-medium">No slides yet</p>
              <p className="text-xs text-zinc-500 mt-1">Upload one above — the idle screen will start cycling them automatically.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2" data-testid="promos-list">
            {promos.map((promo, idx) => (
              <PromoRow
                key={promo.id}
                promo={promo}
                idx={idx}
                isDragOver={dragOverIndex === idx && dragIndex !== idx}
                branches={branches}
                saving={savingId === promo.id}
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDragEnd={onDragEnd}
                onDrop={onDrop(idx)}
                onChange={(patch) => updateRow(promo.id, patch)}
                onSave={() => saveRow(promo)}
                onDelete={() => removeRow(promo)}
              />
            ))}
          </div>
        )}

        {/* Preview tip — what the kiosk will look like */}
        {showHelp && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={() => setShowHelp(false)}>
            <div className="bg-white rounded-2xl max-w-md p-6 space-y-3 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="promos-help-modal">
              <h2 className="font-bold text-lg flex items-center gap-2"><Info size={18} className="text-primary" /> How the rotator works</h2>
              <ol className="list-decimal ml-5 text-sm space-y-1.5 leading-relaxed">
                <li>Open <strong>Cashier POS → ⋯ More → Customer Display</strong> on the second-monitor browser window.</li>
                <li>While the cart is empty the screen loops the <em>active</em> slides in the order you set here.</li>
                <li>The moment the cashier rings up an item, the cart takes over the screen — the rotator pauses automatically and resumes when the cart is cleared.</li>
                <li>Use <em>Branch</em> on a slide to limit it to a single location (e.g. show &ldquo;Riyadh Lunch Combo&rdquo; only on the Riyadh display); leave it blank to show everywhere.</li>
              </ol>
              <div className="flex justify-end pt-1">
                <Button size="sm" onClick={() => setShowHelp(false)} data-testid="promos-help-close">Got it</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

// ── Per-row component ─────────────────────────────────────────────────

function PromoRow({
  promo, idx, isDragOver, branches, saving,
  onDragStart, onDragOver, onDragEnd, onDrop,
  onChange, onSave, onDelete,
}) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      onDrop={onDrop}
      className={`flex items-stretch gap-3 bg-white rounded-xl border ${isDragOver ? 'border-primary ring-2 ring-primary/30' : 'border-zinc-200'} p-3 shadow-sm`}
      data-testid={`promo-row-${idx}`}
    >
      {/* Drag handle */}
      <div className="flex items-center text-zinc-400 cursor-grab active:cursor-grabbing px-1" data-testid={`promo-drag-${idx}`}>
        <GripVertical size={18} />
      </div>

      {/* Thumbnail preview */}
      <div className="relative w-32 h-20 rounded-lg overflow-hidden bg-zinc-100 flex-shrink-0">
        {promo.image_url ? (
          <img src={resolveUploadUrl(promo.image_url)} alt={promo.caption || 'promo'} className="w-full h-full object-cover" />
        ) : (
          <div className="flex items-center justify-center h-full text-zinc-400"><ImageIcon size={20} /></div>
        )}
        {!promo.active && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <EyeOff size={18} className="text-white/90" />
          </div>
        )}
      </div>

      {/* Editable fields */}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-2 items-center">
          <div className="lg:col-span-4">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Caption</Label>
            <Input
              value={promo.caption || ''}
              placeholder="Big headline overlay"
              onChange={(e) => onChange({ caption: e.target.value })}
              className="h-8 text-sm"
              data-testid={`promo-caption-${idx}`}
            />
          </div>
          <div className="lg:col-span-3">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Subtitle</Label>
            <Input
              value={promo.subtitle || ''}
              placeholder="Small subline (optional)"
              onChange={(e) => onChange({ subtitle: e.target.value })}
              className="h-8 text-sm"
              data-testid={`promo-subtitle-${idx}`}
            />
          </div>
          <div className="lg:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Duration (s)</Label>
            <Input
              type="number"
              min={2}
              max={60}
              value={promo.duration_seconds || 6}
              onChange={(e) => onChange({ duration_seconds: parseInt(e.target.value, 10) || 6 })}
              className="h-8 text-sm"
              data-testid={`promo-duration-${idx}`}
            />
          </div>
          <div className="lg:col-span-3">
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Branch</Label>
            <Select
              value={promo.branch_id || 'all'}
              onValueChange={(v) => onChange({ branch_id: v === 'all' ? null : v })}
            >
              <SelectTrigger className="h-8 text-sm" data-testid={`promo-branch-${idx}`}><SelectValue placeholder="All branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* iter705 — Schedule row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
              <Clock size={11} /> Show from
            </Label>
            <Input
              type="datetime-local"
              value={toLocalInput(promo.start_at)}
              onChange={(e) => onChange({ start_at: fromLocalInput(e.target.value) })}
              className="h-8 text-sm"
              data-testid={`promo-start-at-${idx}`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
              <Clock size={11} /> Show until
            </Label>
            <Input
              type="datetime-local"
              value={toLocalInput(promo.end_at)}
              onChange={(e) => onChange({ end_at: fromLocalInput(e.target.value) })}
              className="h-8 text-sm"
              data-testid={`promo-end-at-${idx}`}
            />
          </div>
          <div>
            <PromoScheduleBadge promo={promo} testIdSuffix={idx} />
          </div>
        </div>
      </div>

      {/* Action column */}
      <div className="flex flex-col items-center justify-center gap-1.5 ml-1">
        <button
          type="button"
          title={promo.active ? 'Active — click to pause' : 'Paused — click to activate'}
          onClick={() => { onChange({ active: !promo.active }); }}
          className={`p-1.5 rounded-md ${promo.active ? 'text-emerald-600 hover:bg-emerald-50' : 'text-zinc-400 hover:bg-zinc-100'}`}
          data-testid={`promo-active-${idx}`}
        >
          {promo.active ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={onSave}
          disabled={saving || !promo._dirty}
          data-testid={`promo-save-${idx}`}
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} className="mr-1" />}
          Save
        </Button>
        <button
          type="button"
          onClick={onDelete}
          className="text-rose-500 hover:bg-rose-50 p-1.5 rounded-md"
          title="Delete"
          data-testid={`promo-delete-${idx}`}
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Schedule helpers (iter705) ────────────────────────────────────────
// `<input type="datetime-local">` works in *local* time without a TZ
// offset. We store UTC ISO strings server-side so the rotator filter is
// timezone-agnostic. These helpers bridge the two formats.

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // YYYY-MM-DDTHH:mm in *local* tz.
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

// Live-vs-scheduled-vs-expired pill. Recomputes on the second so a
// happy-hour promo's badge flips correctly while the admin is on the
// page. Avoids re-rendering the whole row.
function PromoScheduleBadge({ promo, testIdSuffix }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30 * 1000);
    return () => clearInterval(id);
  }, []);

  const start = promo.start_at ? new Date(promo.start_at).getTime() : null;
  const end = promo.end_at ? new Date(promo.end_at).getTime() : null;

  let label = 'Always on';
  let cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
  let Icon = CheckCircle2;

  if (!promo.active) {
    label = 'Paused';
    cls = 'bg-zinc-100 text-zinc-600 border-zinc-200';
    Icon = EyeOff;
  } else if (start && now < start) {
    label = `Starts ${new Date(promo.start_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
    cls = 'bg-sky-50 text-sky-700 border-sky-200';
    Icon = Calendar;
  } else if (end && now > end) {
    label = 'Expired';
    cls = 'bg-rose-50 text-rose-700 border-rose-200';
    Icon = XCircle;
  } else if (start || end) {
    label = end
      ? `Ends ${new Date(promo.end_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
      : 'Live now';
    cls = 'bg-emerald-50 text-emerald-700 border-emerald-200';
    Icon = Clock;
  }

  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-[11px] font-semibold ${cls}`} data-testid={`promo-schedule-badge-${testIdSuffix}`}>
      <Icon size={12} />
      {label}
    </div>
  );
}

